import React, { Fragment } from 'react';
import { useQuery } from '@apollo/client';
import { Stack, styled } from '@mui/system';
import { CardBodyGroup, CardTitle, CardBody } from './styles';
import CONSTANTS from './constants';
import dayjs from 'op-utils/dayjs';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { Divider, Typography } from '@mui/material';
import { TENANT_CONFIG_QUERY } from '../../../../components/RO/Base/TenantConfigQuery';
import { getAppointmentDescriptionForDisplay } from 'op-utils/appointmentDescriptionDisplay';

const { NO_UPCOMING } = CONSTANTS;

const ApptHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  background: ${(props) => props.theme.palette.background.secondary};
  font-size: 12px;
  padding: 8px 16px;
`;

const ApptItemWrapper = styled('div')`
  margin-bottom: 8px;
  display: flex;
`;

const ApptLocation = styled('div')`
  display: inline-flex;
  flex-direction: column;
  padding-left: 8px;
`;

interface FormattedAppointment {
  time: string;
  displayTime: string;
  location: string;
  description: string | null;
  department: string;
  duration: number;
}

export interface ReducedAppointment {
  appts: FormattedAppointment[];
  date: string;
  formattedDate: string;
  isToday: boolean;
  isTomorrow: boolean;
}

interface Appointment {
  description?: string;
  shortDescription?: string | null;
  appointmentType?: string;
  id: string;
  startTime: string;
  duration: number;
  location: {
    id: string;
    name: string;
  };
  department?: {
    id: string;
    alias: string;
  };
}

interface Props {
  name: string;
  appointments: Appointment[];
}

export const getReducedAppointments = (
  appointments: Appointment[],
  appointmentDescriptionSource?: string | null,
): any =>
  appointments.reduce((acc: any, curr: any) => {
    const formattedDate = dayjs(curr.startTime).format(CurrentAppConfig.PatientCard.appointmentDateFormat);
    const today = dayjs().format(CurrentAppConfig.PatientCard.appointmentDateFormat);
    const tomorrow = dayjs().add(1, 'days').format(CurrentAppConfig.PatientCard.appointmentDateFormat);

    const apptObj = {
      time: dayjs(curr?.startTime),
      displayTime: dayjs(curr?.startTime).format('h:mmA'),
      description: getAppointmentDescriptionForDisplay(
        {
          description: curr?.description,
          shortDescription: curr?.shortDescription,
        },
        appointmentDescriptionSource,
      ),
      location: curr?.location?.name || '',
      department: curr?.department?.alias || '',
      duration: curr?.duration || 0,
    };

    // Only add appointments from today onwards
    if (dayjs(curr.startTime).diff(dayjs()) > 0) {
      // Group appointments by date in same object
      const existingDateIndex = acc.findIndex((item: ReducedAppointment) => item.formattedDate === formattedDate);
      if (existingDateIndex >= 0) {
        acc[existingDateIndex].appts.push(apptObj);
      } else
        acc.push({
          date: curr.startTime,
          formattedDate: formattedDate,
          isToday: formattedDate === today,
          isTomorrow: formattedDate === tomorrow,
          appts: [apptObj],
          duration: curr?.duration || 0,
        });
    }

    return [...acc].sort(
      (a: ReducedAppointment, b: ReducedAppointment) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, []);

const AppointmentsCard = ({ name, appointments }: Props): JSX.Element => {
  const { data: tenantConfigData } = useQuery(TENANT_CONFIG_QUERY);
  const appointmentsList = appointments || [];
  const reducedSortedAppts =
    appointmentsList.length > 0
      ? getReducedAppointments(appointmentsList, tenantConfigData?.tenantConfig?.appointmentDescriptionSource)
      : [];

  const renderAppointments = (appointmentsArr: FormattedAppointment[]) => {
    const sortedAppts = [...appointmentsArr].sort((a: any, b: any) => a.time - b.time);
    return sortedAppts.map((appt: FormattedAppointment) => (
      <CardBodyGroup key={appt.displayTime}>
        <ApptItemWrapper>
          <Stack direction="column" sx={{ paddingRight: '8px', alignItems: 'flex-end' }}>
            <Typography variant="caption" color="text.primary">
              {appt.displayTime}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {appt.duration / 60}'
            </Typography>
          </Stack>
          <Divider orientation="vertical" flexItem sx={{ color: 'primary.main' }} />
          <ApptLocation>
            {appt.description && <Typography variant="caption">{appt.description}</Typography>}
            <Typography variant="caption" color="text.secondary">
              {appt.department ? appt.department.concat(' - ', appt.location) : appt.location}
            </Typography>
          </ApptLocation>
        </ApptItemWrapper>
      </CardBodyGroup>
    ));
  };
  return (
    <div data-testid="appointmentsCard">
      <CardTitle>{name}</CardTitle>
      <CardBody>
        {reducedSortedAppts.length > 0 ? (
          reducedSortedAppts.map((apptDate: ReducedAppointment) => (
            <Fragment key={apptDate.formattedDate}>
              <ApptHeader>
                <Typography variant="caption" color="text.primary">
                  {apptDate.formattedDate}
                </Typography>
                <Typography variant="caption" color="text.primary">
                  {apptDate.isToday ? 'Today' : apptDate.isTomorrow ? 'Tomorrow' : null}
                </Typography>
              </ApptHeader>
              {renderAppointments(apptDate.appts)}
            </Fragment>
          ))
        ) : (
          <CardBodyGroup>{NO_UPCOMING}</CardBodyGroup>
        )}
      </CardBody>
    </div>
  );
};

export default AppointmentsCard;
